import { CheckCircle, Award, Clock, Star, Monitor, Wifi } from 'lucide-react';

export default function AboutPage() {
  const features = [
    {
      icon: Clock,
      title: 'Desde 2014',
      description: 'Mais de 10 anos de experiência no mercado de tecnologia'
    },
    {
      icon: Award,
      title: 'Formação Técnica',
      description: 'Técnico em Informática e Análise e Desenvolvimento de Sistemas'
    },
    {
      icon: Monitor,
      title: 'Atendimento Online',
      description: 'Agilidade e flexibilidade com alto padrão de qualidade'
    },
    {
      icon: Star,
      title: '100% Positivas',
      description: 'Avaliações refletindo nosso compromisso com excelência'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-blue-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-secondary font-bold tracking-tight text-slate-900 dark:text-white">
              Sobre <span className="text-blue-600">Nós</span>
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-300">
              Conheça a FastFix - sua parceira em soluções de tecnologia
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 md:p-12 space-y-6">
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Somos especialistas em informática, atuando desde <strong className="text-slate-900 dark:text-white">2014</strong> com 
                experiência técnica sólida e formação em <strong className="text-slate-900 dark:text-white">Técnico em Informática</strong> e 
                <strong className="text-slate-900 dark:text-white"> Análise e Desenvolvimento de Sistemas</strong>. Nosso trabalho é 
                focado em consultoria em tecnologia, oferecendo soluções personalizadas e também a venda de 
                produtos relacionados à informática, sempre com orientação clara e responsável.
              </p>
              
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Atuamos exclusivamente de forma <strong className="text-slate-900 dark:text-white">online</strong>, o que nos permite 
                atender com agilidade e flexibilidade, mantendo um alto padrão de qualidade. Além da informática, 
                trabalhamos com <strong className="text-slate-900 dark:text-white">aluguel de Starlink</strong>, porém nosso foco 
                principal continua sendo tecnologia.
              </p>
              
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Todas as nossas recomendações e atendimentos são baseados em experiência real e contam com 
                avaliações <strong className="text-blue-600 dark:text-blue-400">100% positivas</strong>, refletindo nosso compromisso 
                com confiança, transparência e resultados.
              </p>

              {/* CNPJ Badge */}
              <div className="flex items-center gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-600 dark:text-slate-400">
                  CNPJ: <strong className="text-slate-900 dark:text-white">41.932.231/0001-10</strong>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-12 md:py-16 bg-slate-50 dark:bg-slate-900/50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <h2 className="text-3xl md:text-4xl font-secondary font-bold text-center text-slate-900 dark:text-white mb-12">
            Por que escolher a FastFix?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 text-center hover:border-blue-500/30 hover:shadow-lg transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <feature.icon className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-secondary font-semibold text-slate-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-secondary font-bold text-center text-slate-900 dark:text-white mb-12">
            Nossos Serviços
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 hover:border-blue-500/30 transition-colors">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Monitor className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-secondary font-semibold text-slate-900 dark:text-white text-lg">
                  Consultoria em Tecnologia
                </h3>
              </div>
              <p className="text-slate-600 dark:text-slate-400">
                Soluções personalizadas para suas necessidades em informática, com orientação técnica especializada.
              </p>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 hover:border-blue-500/30 transition-colors">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                  <Wifi className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                </div>
                <h3 className="font-secondary font-semibold text-slate-900 dark:text-white text-lg">
                  Aluguel de Starlink
                </h3>
              </div>
              <p className="text-slate-600 dark:text-slate-400">
                Internet via satélite para eventos, viagens ou áreas remotas com planos flexíveis.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
